import React, { useState, useEffect, useContext, useRef } from 'react'
import { Context } from '../../Context'
import { SchedulerService } from '../../services/Scheduler'
import { LaboratoryService } from '../../services/Laboratory'
import { PodsService } from '../../services/Pods'
import Calendar from 'react-calendar'
import Select from 'react-select'
import { DateTime } from 'luxon'
import { Message } from 'primereact/message'
import { Growl } from 'primereact/growl'
import PropTypes from 'prop-types'
import './styles.scss'

export const ScheduleVisualCalendar = ({ onTimeSlotSelect, allowSelection = false }) => {
  const { isAuth: token } = useContext(Context)
  const [selectedDay, setSelectedDay] = useState(DateTime.local().startOf('day'))
  const [selectedLaboratory, setSelectedLaboratory] = useState(null)
  const [selectedPod, setSelectedPod] = useState(null)
  const [laboratories, setLaboratories] = useState([])
  const [pods, setPods] = useState([])
  const [, setScheduleData] = useState([])
  const [availableHours, setAvailableHours] = useState([])
  const [occupiedHours, setOccupiedHours] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const growlRef = useRef(null)
  const abortControllerRef = useRef(new AbortController())
  const isMounted = useRef(true)

  // Cargar laboratorios al montar
  useEffect(() => {
    isMounted.current = true
    loadLaboratories()

    return () => {
      isMounted.current = false
      abortControllerRef.current.abort()
    }
  }, [])

  // Cargar pods cuando se selecciona un laboratorio
  useEffect(() => {
    if (selectedLaboratory) {
      loadPods(selectedLaboratory.value)
    } else {
      setPods([])
      setSelectedPod(null)
      setScheduleData([])
      setAvailableHours([])
      setOccupiedHours([])
    }
  }, [selectedLaboratory])

  // Cargar datos de horarios cuando cambian los filtros (después de que los pods estén cargados)
  useEffect(() => {
    if (selectedLaboratory && selectedDay && pods.length > 0) {
      loadScheduleData()
    } else if (selectedLaboratory && selectedDay && !selectedLaboratory) {
      // Si no hay laboratorio seleccionado, limpiar datos
      setScheduleData([])
      setAvailableHours([])
      setOccupiedHours([])
    }
  }, [selectedLaboratory, selectedDay, selectedPod, pods])

  const loadLaboratories = async () => {
    try {
      setIsLoading(true)
      const data = await LaboratoryService.getAll(token)
      const options = data.map((lab) => ({
        label: lab.name,
        value: lab.id,
      }))
      if (isMounted.current) {
        setLaboratories(options)
      }
    } catch (e) {
      if (isMounted.current) {
        setError('Error al cargar laboratorios: ' + e.message)
      }
    } finally {
      if (isMounted.current) {
        setIsLoading(false)
      }
    }
  }

  const loadPods = async (laboratoryId) => {
    try {
      setIsLoading(true)
      const data = await PodsService.getFreepods(laboratoryId, token)
      if (data && data.freePods) {
        const options = data.freePods.map((pod) => ({
          label: pod.name,
          value: pod.id,
        }))
        if (isMounted.current) {
          setPods(options)
        }
      }
    } catch (e) {
      if (isMounted.current) {
        setError('Error al cargar pods: ' + e.message)
      }
    } finally {
      if (isMounted.current) {
        setIsLoading(false)
      }
    }
  }

  const loadScheduleData = async () => {
    if (!selectedLaboratory || !selectedDay) return

    try {
      setIsLoading(true)
      setError(null)

      // Abortar solicitud anterior si existe
      abortControllerRef.current.abort()
      abortControllerRef.current = new AbortController()

      const laboratoryId = selectedLaboratory.value
      const selectedDateUTC = selectedDay.toUTC()
      const nextDateUTC = selectedDay.plus({ days: 1 }).toUTC()

      // Obtener datos del día actual y del siguiente
      const signal = abortControllerRef.current.signal
      const [currentDayData, nextDayData] = await Promise.all([
        SchedulerService.getByDay(selectedDateUTC.toISO(), laboratoryId, token, { signal }),
        SchedulerService.getByDay(nextDateUTC.toISO(), laboratoryId, token, { signal }),
      ])

      if (!isMounted.current) return

      // Combinar datos de ambos días
      const allSchedules = [
        ...(Array.isArray(currentDayData) ? currentDayData : []),
        ...(Array.isArray(nextDayData) ? nextDayData : []),
      ]

      // Procesar horarios ocupados y disponibles
      processScheduleData(allSchedules)
    } catch (e) {
      if (e.name === 'AbortError') return
      if (isMounted.current) {
        setError('Error al cargar horarios: ' + e.message)
        setScheduleData([])
        setAvailableHours([])
        setOccupiedHours([])
      }
    } finally {
      if (isMounted.current) {
        setIsLoading(false)
      }
    }
  }

  const processScheduleData = (schedules) => {
    // Obtener todos los pods del laboratorio para generar horas disponibles
    const allPods = pods.length > 0 ? pods : []

    // Si hay un pod seleccionado, filtrar solo ese pod
    const podsToProcess = selectedPod
      ? allPods.filter((p) => p.value === selectedPod.value)
      : allPods

    // Generar todas las horas del día (0-23)
    const today = DateTime.local()
    const isToday = selectedDay.hasSame(today, 'day')
    const startHour = isToday
      ? process.env.REACT_APP_IS_DEVELOPMENT === 'true'
        ? today.hour
        : today.hour + 1
      : 0

    // Convertir horarios ocupados a hora local
    const occupied = []
    const occupiedByPod = {}

    schedules.forEach((schedule) => {
      const startDate = DateTime.fromISO(schedule.startDate, { zone: 'utc' }).toLocal()
      const endDate = DateTime.fromISO(schedule.endDate, { zone: 'utc' }).toLocal()

      // Solo procesar si es del día seleccionado
      if (startDate.hasSame(selectedDay, 'day') || endDate.hasSame(selectedDay, 'day')) {
        const podId = schedule.pod_id

        // Inicializar array para este pod si no existe
        if (!occupiedByPod[podId]) {
          occupiedByPod[podId] = []
        }

        // Agregar todas las horas ocupadas en este rango
        for (let hour = startDate.hour; hour < endDate.hour; hour++) {
          const hourKey = `${hour}:00`
          if (!occupiedByPod[podId].includes(hourKey)) {
            occupiedByPod[podId].push(hourKey)
          }
          if (!occupied.some((o) => o.hour === hour && o.podId === podId)) {
            occupied.push({
              hour,
              hourKey,
              podId,
              podName: schedule.pod?.name || `Pod ${podId}`,
              startDate: startDate.toISO(),
              endDate: endDate.toISO(),
              scheduleId: schedule.id,
            })
          }
        }
      }
    })

    // Generar horas disponibles para cada pod
    const available = []
    podsToProcess.forEach((pod) => {
      for (let hour = startHour; hour < 24; hour++) {
        const hourKey = `${hour}:00`
        const isOccupied = occupiedByPod[pod.value]?.includes(hourKey) || false

        if (!isOccupied) {
          available.push({
            hour,
            hourKey,
            podId: pod.value,
            podName: pod.label,
            date: selectedDay.set({ hour }).toISO(),
          })
        }
      }
    })

    if (isMounted.current) {
      setScheduleData(schedules)
      setOccupiedHours(occupied)
      setAvailableHours(available)
    }
  }

  const handleDateChange = (date) => {
    const luxonDate = DateTime.fromJSDate(date).startOf('day')
    setSelectedDay(luxonDate)
  }

  const handleLaboratoryChange = (option) => {
    setSelectedLaboratory(option)
    setSelectedPod(null)
  }

  const handlePodChange = (option) => {
    setSelectedPod(option)
  }

  // Agrupar horarios por hora para mostrar en la lista
  const getGroupedHours = () => {
    const grouped = {}

    // Agregar horas ocupadas
    occupiedHours.forEach((item) => {
      const key = item.hourKey
      if (!grouped[key]) {
        grouped[key] = {
          hour: key,
          hourNum: item.hour,
          occupied: [],
          available: [],
        }
      }
      if (!grouped[key].occupied.find((o) => o.podId === item.podId)) {
        grouped[key].occupied.push(item)
      }
    })

    // Agregar horas disponibles
    availableHours.forEach((item) => {
      const key = item.hourKey
      if (!grouped[key]) {
        grouped[key] = {
          hour: key,
          hourNum: item.hour,
          occupied: [],
          available: [],
        }
      }
      if (!grouped[key].available.find((a) => a.podId === item.podId)) {
        grouped[key].available.push(item)
      }
    })

    // Ordenar por hora
    return Object.values(grouped).sort((a, b) => a.hourNum - b.hourNum)
  }

  const groupedHours = getGroupedHours()

  return (
    <div className="schedule-visual-calendar">
      <Growl ref={growlRef} />

      {/* Filtros */}
      <div className="filters-container">
        <div className="filter-item">
          <label>Laboratorio</label>
          <Select
            placeholder="Seleccione un laboratorio"
            options={laboratories}
            value={selectedLaboratory}
            onChange={handleLaboratoryChange}
            isClearable
            isSearchable
            className="filter-select"
            classNamePrefix="select"
          />
        </div>

        {selectedLaboratory && (
          <div className="filter-item">
            <label>POD (Opcional)</label>
            <Select
              placeholder="Todos los PODs"
              options={pods}
              value={selectedPod}
              onChange={handlePodChange}
              isClearable
              isSearchable
              className="filter-select"
              classNamePrefix="select"
            />
          </div>
        )}
      </div>

      {error && <Message severity="error" text={error} style={{ marginBottom: '20px' }} />}

      {/* Contenedor principal: Calendario a la izquierda, Lista a la derecha */}
      <div className="schedule-content">
        {/* Calendario - Lado izquierdo */}
        <div className="calendar-section">
          <h3>Seleccione una fecha</h3>
          <Calendar
            className="calendar-container"
            minDate={new Date()}
            onChange={handleDateChange}
            value={selectedDay ? selectedDay.toJSDate() : new Date()}
            locale="es"
          />
        </div>

        {/* Lista de horarios - Lado derecho */}
        <div className="schedule-list-section">
          <h3>
            Horarios{' '}
            {selectedDay
              ? `- ${selectedDay.toLocaleString({
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                })}`
              : ''}
          </h3>

          {isLoading ? (
            <div className="loading-container">
              <i className="pi pi-spin pi-spinner" style={{ fontSize: '2em' }}></i>
              <p>Cargando horarios...</p>
            </div>
          ) : !selectedLaboratory ? (
            <div className="empty-state">
              <i className="pi pi-info-circle" style={{ fontSize: '3em', color: '#ccc' }}></i>
              <p>Seleccione un laboratorio para ver los horarios</p>
            </div>
          ) : pods.length === 0 ? (
            <div className="empty-state">
              <i
                className="pi pi-exclamation-triangle"
                style={{ fontSize: '3em', color: '#ff9800' }}
              ></i>
              <p>No hay pods disponibles en este laboratorio</p>
            </div>
          ) : groupedHours.length === 0 ? (
            <div className="empty-state">
              <i className="pi pi-calendar-times" style={{ fontSize: '3em', color: '#ccc' }}></i>
              <p>No hay horarios disponibles para esta fecha</p>
            </div>
          ) : (
            <div className="schedule-list">
              {groupedHours.map((group) => (
                <div key={group.hour} className="hour-group">
                  <div className="hour-header">
                    <span className="hour-label">{group.hour}</span>
                    {group.available.length > 0 && (
                      <span className="status-badge available">
                        {group.available.length} Disponible{group.available.length > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

ScheduleVisualCalendar.propTypes = {
  onTimeSlotSelect: PropTypes.func,
  allowSelection: PropTypes.bool,
}
